import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import i18n from '@/i18n';
import { normalizeLang, siteLanguage, saveAccountLanguage } from '@/lib/accountLanguage';
import { isPrivatePath } from '@/i18n/urlLanguage';
import { markUserActivity } from '@/lib/userActivity';
import { clearAttemptId } from '@/lib/refreshAttempt';
import { unsubscribeLocal } from '@/lib/push';
import { hideSplash } from '@/lib/splash';
import {
  hasPendingLogout, isAuthRejection, onSessionExpired, resetSessionExpired, setPendingLogout,
} from '@/lib/sessionEvents';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Maps a login/register API error to a translation key (rendered with t() by
// the page). Raw server or network messages are never shown to the user.
const AUTH_ERROR_KEYS = {
  'Invalid email or password': 'auth.errors.invalidCredentials',
  'Email already registered': 'auth.errors.emailAlreadyRegistered',
  'Too many failed attempts. Try again later.': 'auth.errors.tooManyAttempts',
};

function authErrorKey(error) {
  const response = error?.response;
  if (!response) return 'auth.errors.network';
  const detail = response.data?.detail;
  if (typeof detail === 'string' && AUTH_ERROR_KEYS[detail]) return AUTH_ERROR_KEYS[detail];
  if (response.status === 429) return 'auth.errors.tooManyAttempts';
  if (response.status === 422) return 'auth.errors.invalidInput';
  return 'auth.errors.generic';
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // The session could not be checked (offline, server down / 5xx). This is NOT
  // a sign-out: cookies are untouched and the check is retried automatically.
  // The last known `user` is kept; the server still decides every right.
  const [authUnavailable, setAuthUnavailable] = useState(false);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the OAuth code and establish the session first.
    if (window.location.search?.includes('code=') || window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      // A sign-out that could not reach the server earlier is replayed first,
      // otherwise the still-valid cookies would sign the user back in.
      if (hasPendingLogout()) {
        await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        setPendingLogout(false);
      }
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      resetSessionExpired();
      setUser(data);
      setAuthUnavailable(false);
    } catch (error) {
      if (hasPendingLogout()) {
        // Logout replay failed: stay signed out locally, retry later.
        setUser(null);
        setAuthUnavailable(true);
      } else if (isAuthRejection(error)) {
        // 401/403: the server says there is no valid session.
        setUser(null);
        setAuthUnavailable(false);
      } else {
        // Network error, timeout, 5xx: keep what we know, retry later.
        setAuthUnavailable(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // The session check is over (signed in, signed out, or unavailable: the matching
  // screen is rendered) -> drop the installed-app startup screen. No delay.
  useEffect(() => {
    if (!loading) hideSplash();
  }, [loading]);

  // The refresh token was rejected while the app was in use: the session is
  // over. Clearing the user makes ProtectedRoute redirect once to
  // /login?redirect=<current page> and stops the per-user pollers.
  useEffect(() => onSessionExpired(() => setUser(null)), []);

  // Back in the foreground after a while (installed app reopened, tab revisited):
  // one /auth/me flagged as user activity, at most every 5 minutes. It renews the
  // session's inactivity window (and the access token through the usual 401 ->
  // refresh path). Failures are ignored here: the Lot A handlers deal with them.
  const hasUser = Boolean(user);
  useEffect(() => {
    if (!hasUser) return undefined;
    let lastPing = Date.now();
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || Date.now() - lastPing < 5 * 60 * 1000) return;
      lastPing = Date.now();
      markUserActivity();
      axios.get(`${API}/auth/me`, { withCredentials: true }).catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [hasUser]);

  // While the check is unavailable: retry when the network comes back, when the
  // tab becomes visible again, and with a growing delay (3 s … 60 s).
  useEffect(() => {
    if (!authUnavailable) return undefined;
    let attempt = 0;
    let timer;
    const retry = () => { clearTimeout(timer); checkAuth(); };
    const schedule = () => {
      timer = setTimeout(() => { retry(); attempt += 1; schedule(); }, Math.min(3000 * 2 ** attempt, 60000));
    };
    const onVisible = () => { if (document.visibilityState === 'visible') retry(); };
    schedule();
    window.addEventListener('online', retry);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('online', retry);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [authUnavailable, checkAuth]);

  // Account language (decides the language of the e-mails the user receives):
  // always the language the signed-in user is using on Ridalo. When the site
  // is shown in another language than the one stored on the account (at sign-
  // in, on each visit, or after a switch), the site's language is saved.
  // The server's value never changes the site's language.
  // No request when both match; one request at a time per language.
  const userId = user?.user_id;
  const storedLang = normalizeLang(user?.preferred_language);
  const savingLang = useRef(null);
  const onPrivatePage = isPrivatePath(useLocation().pathname);
  useEffect(() => {
    if (!userId) return undefined;
    const sync = (lng) => {
      // Public pages show the URL's language, not a choice of the user: the
      // account language follows the language used on private pages only.
      if (!isPrivatePath(window.location.pathname)) return;
      const lang = normalizeLang(lng);
      if (!lang || lang === storedLang || lang === savingLang.current) return;
      savingLang.current = lang;
      saveAccountLanguage(lang).then((saved) => {
        if (savingLang.current === lang) savingLang.current = null;
        if (saved) setUser((u) => (u && u.user_id === userId ? { ...u, preferred_language: saved } : u));
      });
    };
    sync(siteLanguage());
    i18n.on('languageChanged', sync);
    return () => i18n.off('languageChanged', sync);
  }, [userId, storedLang, onPrivatePage]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      resetSessionExpired();
      clearAttemptId();
      setPendingLogout(false);
      setAuthUnavailable(false);
      setUser(data);
      return { success: true, data };
    } catch (e) {
      // `error` is a translation key, e.g. "auth.errors.invalidCredentials"
      return { success: false, error: authErrorKey(e) };
    }
  };

  const register = async (email, password, name, role) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, { email, password, name, role, language: siteLanguage() }, { withCredentials: true });
      resetSessionExpired();
      clearAttemptId();
      setPendingLogout(false);
      setAuthUnavailable(false);
      setUser(data);
      return { success: true, data };
    } catch (e) {
      // `error` is a translation key, e.g. "auth.errors.emailAlreadyRegistered"
      return { success: false, error: authErrorKey(e) };
    }
  };

  // Clears the cookies server-side (works even when the access token has
  // expired). If the server cannot be reached the sign-out is remembered and
  // replayed at the next check, so a reload cannot silently sign the user back in.
  // LIMIT: tokens are stateless JWTs, this does not revoke a copied refresh token.
  const logout = async () => {
    await unsubscribeLocal();          // this device stops receiving the account's notifications (the server also drops it with the session)
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setPendingLogout(false);
    } catch (e) {
      console.error('Logout error:', e);
      setPendingLogout(true);
    } finally {
      clearAttemptId();
      setUser(null);
    }
  };

  const googleLogin = (role) => {
    // Direct Google OAuth flow using custom Ridalo credentials
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = encodeURIComponent(window.location.origin + '/google-callback');
    const scope = encodeURIComponent('openid email profile');
    const state = encodeURIComponent(JSON.stringify({ role }));
    
    // Redirect directly to Google OAuth
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&state=${state}&access_type=offline&prompt=consent`;
  };

  return (
    <AuthContext.Provider value={{ user, loading, authUnavailable, login, register, logout, googleLogin, checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};